/**
 * @fileoverview The Plants page's side of the sharing moments.
 *
 * "Share your cherry tomato?" once, the first time a plant has a photograph,
 * with that photograph and two answers; Share… opens the ordinary share
 * sheet. Once per account: the question is asked only when the server places
 * it (the first_share advice), and the answer is the server's mark, so a new
 * browser never asks again (audit A70). When the advice has arrived and the
 * server places none, the pending plant is forgotten. And the photo screen for a plant, when the add form's "Add photo"
 * or an empty share sheet asks for it.
 *
 * Lives on the page rather than on each card so it works in every view
 * (cards, grid, list) and for a plant filed from the photo tray.
 *
 * Where: the Plants page (PlantGrid), once, for every view.
 * API: none of its own (the advice's seen, done and put away go through
 * useTips: POST /todo/marks); the share sheet and the photo dialog make theirs.
 * Settings: the bottom_panel advice in the Grow settings' rules list, placed
 * by the server (todo.reads.bottom_panel; off there, never asked);
 * the pending plant handed between screens (shareMoments,
 * FIRST_SHARE_PENDING_STORAGE_KEY in config/tunables/garden).
 * Words (en/es): the advice's words ({plant} put in here); share.moment.notNow, .share, .once.
 *
 * @module ShareMoments
 */

import React from 'react';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAuth } from '@/shared/hooks/useAuth';
import type { Plant } from '@/shared/types/plant';
import type { PlantPhoto } from '@/shared/types/photo';
import { SharePlantDialog } from '@/features/plant-list/components/dialogs/SharePlantDialog';
import { PhotoUploadDialog } from '@/features/plant-list/components/dialogs/PhotoUploadDialog';
import { photoUrl } from '@/features/plant-list/lib/plantPhotoUrl';
import { useTips } from '@/features/tips/useTips';
import {
  ADD_PHOTO_EVENT, FIRST_SHARE_EVENT, firstShareAnswered, pendingFirstShare,
} from './lib/shareMoments';
import { plantCaptionStarter } from '@/features/plant-list/lib/captionStarter';
import { useGardenSeason } from '@/shared/hooks/useTodayBrief';
import { plantName, shareWords } from '@/shared/lib/plantName';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';

/**
 * A plant's photographs, typed as the share sheet reads them.
 *
 * @param plant - The plant, or null.
 */
const photosOf = (plant: Plant | null): PlantPhoto[] => ((plant?.photos ?? []) as unknown as PlantPhoto[]);

/**
 * The newest photograph, as the share sheet leads with it.
 *
 * @param photos - The plant's photographs.
 * @returns The one taken last, or null.
 */
const newest = (photos: PlantPhoto[]): PlantPhoto | null =>
  [...photos].sort((a, b) => String(b.date_taken ?? '').localeCompare(String(a.date_taken ?? '')))[0] ?? null;

/**
 * The moments Grow offers to share (a first photo) and add a photo, listening for their events.
 *
 * @param props.plants - The Plants page's plants, to find the one an event names.
 */
export const ShareMoments: React.FC<{ plants: Plant[] }> = ({ plants }) => {
  const { reactTranslate: t } = useTranslation();
  const { mysqlUser } = useAuth();
  const [askId, setAskId] = React.useState<number | null>(() => pendingFirstShare());
  const [shareId, setShareId] = React.useState<number | null>(null);
  const [photoId, setPhotoId] = React.useState<number | null>(null);
  const tips = useTips();

  React.useEffect(() => {
    /** A first photo somewhere: ask about that plant. */
    const onFirst = (event: Event): void => setAskId((event as CustomEvent<number>).detail);
    /** A plant wants a photo: open the photo screen for it. */
    const onPhoto = (event: Event): void => setPhotoId((event as CustomEvent<number>).detail);
    window.addEventListener(FIRST_SHARE_EVENT, onFirst);
    window.addEventListener(ADD_PHOTO_EVENT, onPhoto);
    return () => {
      window.removeEventListener(FIRST_SHARE_EVENT, onFirst);
      window.removeEventListener(ADD_PHOTO_EVENT, onPhoto);
    };
  }, []);

  /** A plant of the page's by its id, or null. */
  const find = (id: number | null): Plant | null => (id === null ? null : plants.find((plant) => plant.plant_obj_id === id) ?? null);
  const asking = find(askId);
  const askPhoto = newest(photosOf(asking));
  // The question and its words are the advice the server placed for this moment; switched off there, never asked.
  const tip = askId === null ? null : tips.read('bottom_panel');
  // Ask only once the plant and its photograph have arrived on this page.
  const askOpen = asking !== null && askPhoto !== null && tip !== null;
  const words = tip ? tips.words(tip, { plant: plantName(asking) }) : null;
  React.useEffect(() => { if (askOpen && tip) tips.mark(tip, 'seen'); }, [askOpen, tip, tips]);
  // The advice is in and the server asks nothing (answered on any device, or switched off): forget the plant.
  React.useEffect(() => {
    if (askId === null || !tips.ready || tip !== null) return;
    firstShareAnswered();
    setAskId(null);
  }, [askId, tips.ready, tip]);

  /**
   * The first-share question answered: share now, or not.
   *
   * @param share - True for Share…, false for Not now (or the panel closed).
   */
  const answer = (share: boolean): void => {
    firstShareAnswered();
    if (tip) tips.mark(tip, share ? 'done' : 'put_away');
    if (share) setShareId(askId);
    setAskId(null);
  };

  const sharing = find(shareId);
  // The garden's season for the caption starter: the server's (useGardenSeason).
  const gardenSeason = useGardenSeason(Boolean(sharing));
  const photographing = find(photoId);

  return (
    <>
      <BottomPanel
        open={askOpen}
        onOpenChange={(open) => { if (!open) answer(false); }}
        title={words?.title ?? ''}
      >
        <p className="text-sm text-muted-foreground">{words?.body}</p>
        {askPhoto && (
          <div className="relative mt-3 h-40 w-full overflow-hidden rounded-md bg-muted data-[small=true]:bg-transparent">
            <FramedPhoto src={photoUrl(askPhoto, 'card')} alt="" />
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => answer(false)}>{t('share.moment.notNow')}</Button>
          <Button className="flex-1" onClick={() => answer(true)}>{words?.button || t('share.moment.share')}</Button>
        </div>
        <p className="mt-2 text-center text-xs text-muted-foreground">{t('share.moment.once')}</p>
      </BottomPanel>

      {sharing && (
        <SharePlantDialog
          isOpen
          onClose={() => setShareId(null)}
          plantObjId={sharing.plant_obj_id}
          name={shareWords(sharing).name}
          variety={shareWords(sharing).variety}
          // The first title names the whole plant ("Share Loose-leaf Lettuce"), not only its own words.
          title={t('plants.share.titleFor', { plant: plantName(sharing) })}
          photos={photosOf(sharing)}
          startPhotoId={newest(photosOf(sharing))?.photo_id ?? null}
          captionStarter={plantCaptionStarter(sharing, gardenSeason, t)}
        />
      )}

      {photographing && mysqlUser && (
        <PhotoUploadDialog
          isOpen
          onClose={() => setPhotoId(null)}
          userId={mysqlUser.user_id}
          plantObjId={photographing.plant_obj_id}
          onPhotoUploaded={() => { /* the dialog refreshes the plant itself */ }}
        />
      )}
    </>
  );
};
